import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/utils";
import { useSubscription } from "@/hooks/use-subscription";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Check, Star } from "lucide-react";

interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubscribe?: (planId: number) => void;
  onCancel?: () => void;
}

export function SubscriptionModal({
  open,
  onOpenChange,
  onSubscribe,
  onCancel,
}: SubscriptionModalProps) {
  const { 
    plans, 
    currentPlan, 
    isPendingSubscribe, 
    isPendingCheckout,
    createCheckoutSession 
  } = useSubscription();
  
  const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(
    currentPlan?.id
  );

  // Update selected plan when current plan changes
  useEffect(() => {
    if (currentPlan?.id) {
      setSelectedPlanId(currentPlan.id);
    } else if (plans.length > 0) {
      setSelectedPlanId(plans[0].id);
    }
  }, [currentPlan, plans]);

  const handleSubscribe = () => {
    if (selectedPlanId) {
      if (onSubscribe) {
        // Use the regular subscription flow if provided
        onSubscribe(selectedPlanId);
      } else {
        // Use Stripe checkout
        createCheckoutSession(selectedPlanId);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
              <Star className="h-6 w-6 text-primary-600" />
            </div>
            Upgrade Your Subscription
          </DialogTitle>
          <p className="text-center text-sm text-neutral-500 mt-2">
            Unlock advanced features and AI capabilities with our premium plans.
          </p>
        </DialogHeader>

        <div className="mt-4">
          <RadioGroup
            value={selectedPlanId?.toString() || ""}
            onValueChange={(value) => setSelectedPlanId(parseInt(value))}
            className="space-y-4"
          >
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan?.id === plan.id;
              const isRecommended = plan.name.toLowerCase().includes("pro");

              return (
                <div
                  key={plan.id}
                  className={`flex items-center p-4 border rounded-lg ${
                    isRecommended
                      ? "border-primary-200 bg-primary-50"
                      : "border-neutral-200"
                  }`}
                >
                  <RadioGroupItem
                    value={plan.id.toString()}
                    id={`plan-${plan.id}`}
                    className="h-4 w-4"
                  />
                  <Label
                    htmlFor={`plan-${plan.id}`}
                    className="ml-3 flex-1 cursor-pointer"
                  >
                    <span className="block text-sm font-medium text-neutral-900">
                      {plan.name}
                    </span>
                    <span className="block text-sm text-neutral-500">
                      {formatCurrency(plan.price)}/month
                      {isCurrentPlan && " - Current Plan"}
                    </span>
                  </Label>
                  {isRecommended && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Recommended
                    </span>
                  )}
                  {isCurrentPlan && (
                    <span className="text-sm font-medium text-primary-600">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </div>
              );
            })}
          </RadioGroup>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-6">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="w-full"
            onClick={handleSubscribe}
            disabled={!selectedPlanId || isPendingSubscribe || isPendingCheckout}
          >
            {isPendingSubscribe || isPendingCheckout
              ? "Processing..."
              : currentPlan
              ? "Change Subscription"
              : "Upgrade Subscription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
